import React, { useEffect, useState } from 'react';
import {
  Box,
  MuiBox,
  Drawer,
  Typography,
  Button,
  SvgIconButton,
  Switch,
  DotProgress,
  Card,
  Backdrop,
  FlexBox,
  InputField,
  Grid,
  SelectDropdown,
  color,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import { actions as integrationsActions, selectors as integrationsSelectors, Integration } from 'store/integrations';
import { actions as kioskActions, selectors as kioskSelectors } from 'store/kiosks';
import { actions as configActions, selectors as configSelectors, Configuration } from 'store/configurations';
import { actions as CIActions, selectors as CISelectors, ClientIntegration } from 'store/clientIntegrations';
import { actions as areaActions, selectors as areaSelectors, Area } from 'store/areas';
import { useStyles } from '../../styled';
import { isValidData, getDataTypedValues, formatConfigTitle } from '../../utils';
import { ConfigEditDrawerProps, PropertyType } from '../../types';
import get from 'lodash.get';
import { getSortedList } from 'utils/stringFormat';

export const ConfigEditDrawer = (props: ConfigEditDrawerProps) => {
  const {
    isOpen,
    onClose,
    configData,
    selectedColumn,
    selectedColumnValue,
    selectedProperty: selProperty,
    selectedSubProperty,
    selectedPropertyValue,
    selectedDataType: selDataType,
    configType,
    addConfigDropdown
  } = props;

  const [
    updateClientIntegration,
    resetCIError,
    updateKiosk,
    resetKioskError,
    updateConfiguration,
    resetConfigError,
    resetIntegrationError,
    resetAreaError
  ] = useActions([
    CIActions.updateClientIntegration,
    CIActions.resetError,
    kioskActions.updateKiosk,
    kioskActions.resetAction,
    configActions.updateConfiguration,
    configActions.resetError,
    integrationsActions.resetError,
    areaActions.resetError
  ]);
  const { error: integrationError, pending: integrationPending } = useSelector(integrationsSelectors.selectAction);
  const { error: areaError, pending: areaPending } = useSelector(areaSelectors.selectAction);
  const ciError = useSelector(CISelectors.selectError);
  const ciPending = useSelector(CISelectors.selectPending);
  const { error: kioskError, pending: kioskPending } = useSelector(kioskSelectors.selectAction);
  const { error: configError, pending: configPending } = useSelector(configSelectors.selectAction);
  const integrations = useSelector(integrationsSelectors.selectIntegrations);
  const configurations = useSelector(configSelectors.selectConfigurations);
  const clientIntegrations = useSelector(CISelectors.selectClientIntegrations);
  const areas = useSelector(areaSelectors.selectAreas);

  const columnKeys = addConfigDropdown
    .reduce((acc: string[], cur) => {
      acc.push(cur.key);
      return acc;
    }, [])
    .sort();

  const [propertyValue, setPropertyValue] = useState<PropertyType>('');
  const [error, setError] = useState<string>('');
  const [apiLoading, setAPILoading] = useState(false);
  const [uiLoading, setUILoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(selProperty);
  const [selectedDataType, setSelectedDataType] = useState(selDataType);
  const [selectedConfigColumn, setSelectedConfigColumn] = useState(columnKeys[0]);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration>(propertyValue as Integration);
  const [selectedConfiguration, setSelectedConfiguration] = useState<Configuration>(propertyValue as Configuration);
  const [selectedCI, setSelectedCI] = useState<ClientIntegration>(propertyValue as ClientIntegration);
  const [selectedArea, setSelectedArea] = useState<Area>(propertyValue as Area);

  const classes = useStyles({ showDrawer: isOpen })();

  useEffect(() => {
    if (propertyValue && selectedDataType === 'integration') {
      setSelectedIntegration(JSON.parse(propertyValue as string) as Integration);
    } else if (propertyValue && selectedDataType === 'configuration') {
      setSelectedConfiguration(JSON.parse(propertyValue as string) as Configuration);
    } else if (propertyValue && selectedDataType === 'clientIntegration') {
      setSelectedCI(JSON.parse(propertyValue as string) as ClientIntegration);
    } else if (propertyValue && selectedDataType === 'area') {
      setSelectedArea(JSON.parse(propertyValue as string) as Area);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyValue]);

  useEffect(() => {
    if (selectedPropertyValue && typeof selectedPropertyValue === 'object') {
      setPropertyValue(JSON.stringify(selectedPropertyValue));
    } else {
      setPropertyValue(selectedPropertyValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyValue]);

  useEffect(() => {
    if (selectedConfigColumn) {
      setSelectedProperty(selectedConfigColumn);
      setSelectedDataType(getSelectedType(selectedConfigColumn));
      if (configType === 'configuration' && selectedColumn === 'cityPass' && selectedConfigColumn === 'ticketing') {
        const cpTicketing = get(configData as Configuration, 'cityPass.ticketing');
        if (!cpTicketing || (cpTicketing && !Object.keys(cpTicketing).length)) {
          const ticketingCopy = get(configData as Configuration, 'ticketing');
          const { membershipLookup, membershipSalesSettingsList, showPriceRange, promoCode, ...cpTicketingCopy } =
            ticketingCopy;
          setPropertyValue(JSON.stringify(cpTicketingCopy));
        }
      } else if (
        configType === 'configuration' &&
        selectedColumn === 'souvenir' &&
        selectedConfigColumn === 'ticketing'
      ) {
        const souvenirTicketing = get(configData as Configuration, 'souvenir.ticketing');
        if (!souvenirTicketing || !Object.keys(souvenirTicketing).length) {
          const ticketingCopy = get(configData as Configuration, 'ticketing');
          const { membershipLookup, membershipSalesSettingsList, showPriceRange, promoCode, ...souvenirTicketingCopy } =
            ticketingCopy;
          setPropertyValue(JSON.stringify(souvenirTicketingCopy));
        }
      } else {
        setPropertyValue('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConfigColumn]);

  useEffect(() => {
    if (ciError || kioskError || configError || areaError || integrationError) {
      setError((ciError || kioskError || configError || areaError || integrationError) as string);
      setUILoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ciError, kioskError, configError, areaError, integrationError]);

  useEffect(() => {
    setAPILoading((ciPending || kioskPending || configPending || areaPending || integrationPending) as boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ciPending, kioskPending, configPending, areaPending, integrationPending]);

  useEffect(() => {
    if (uiLoading && !apiLoading && !error) {
      handleOverlayClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, apiLoading]);

  const getPayload = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let payload: any = {
      id: configData && configData.id
    };
    switch (selectedDataType) {
      case 'integration':
        payload = { ...payload, integrationId: selectedIntegration.id };
        break;
      case 'clientIntegration':
        payload = { ...payload, clientIntegrationId: selectedCI.id };
        break;
      case 'area':
        payload = { ...payload, areaId: selectedArea.id };
        break;
      case 'configuration':
        payload = { ...payload, configurationId: selectedConfiguration.id };
        break;
      default:
        if (selectedSubProperty && typeof selectedColumnValue === 'object') {
          payload = {
            ...payload,
            [selectedColumn]: {
              ...selectedColumnValue,
              [selectedSubProperty]: getDataTypedValues(
                selectedDataType,
                propertyValue,
                selectedProperty,
                selectedSubProperty,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (selectedColumnValue as Record<string, any>)[selectedSubProperty]
              )
            }
          };
        } else {
          payload = {
            ...payload,
            [selectedColumn]: getDataTypedValues(
              selectedDataType,
              propertyValue,
              selectedProperty,
              selectedColumn,
              selectedColumnValue
            )
          };
        }
        break;
    }

    return payload;
  };

  const onConfirm = () => {
    resetError();
    if (isValidData(propertyValue, selectedDataType)) {
      setUILoading(true);
      const payload = getPayload();
      if (configType === 'configuration') {
        updateConfiguration(payload);
      } else if (configType === 'kiosk') {
        updateKiosk(payload);
      } else if (configType === 'clientIntegration') {
        updateClientIntegration(payload);
      }
    } else {
      setError('Invalid data for the property');
    }
  };

  const handleOverlayClose = () => {
    resetError();
    onClose();
  };

  const resetError = () => {
    if (error) {
      setError('');
      if (areaError) {
        resetAreaError();
      } else if (ciError) {
        resetCIError();
      } else if (kioskError) {
        resetKioskError();
      } else if (configError) {
        resetConfigError();
      } else if (integrationError) {
        resetIntegrationError();
      }
    }
  };

  const getSelectedType = (key: string): string => {
    const selected = addConfigDropdown.find((column) => column.key === key);
    return selected ? selected.type : 'object';
  };

  return (
    <>
      {apiLoading && (
        <Backdrop open={apiLoading ? true : false}>
          <DotProgress color={color.GRAY} />
        </Backdrop>
      )}
      <Drawer open={isOpen} onClose={handleOverlayClose} hasBorderRadius>
        <Grid container className={classes.configEditOverlay}>
          <Card fullHeight>
            <Grid className={classes.configOverlayHeaderBlock}>
              <Grid className={classes.configOverlayHeading}>
                <FlexBox>
                  <Typography fontWeight={600} className={classes.configEditHeading}>
                    {configData && configData.name}
                  </Typography>
                </FlexBox>
              </Grid>
              <Grid className={classes.configOverlayCloseIcon}>
                <SvgIconButton appearance="transparent" onClick={handleOverlayClose} data-testid="kiosk-restock-x">
                  <SvgIcon name="exit_circle" size={40} />
                </SvgIconButton>
              </Grid>
            </Grid>
            <Box padding="2 6" fullWidth bg="light-gray">
              <Typography fontWeight={600} fontSize={16}>
                {formatConfigTitle(selectedColumn, selectedDataType)}
                {selectedSubProperty ? ` / ${formatConfigTitle(selectedSubProperty)}` : ''}
              </Typography>
            </Box>
            <MuiBox className={classes.configContentBox}>
              <Box margin="6 0 5" flexOverride="none">
                {addConfigDropdown && addConfigDropdown.length ? (
                  <SelectDropdown
                    items={columnKeys ? columnKeys : ['']}
                    itemToString={(column: string) => column}
                    selectedItem={selectedConfigColumn}
                    onChange={(column: string) => setSelectedConfigColumn(column)}
                    placeholder="Select a Property"
                    data-testid="media-selector"
                  />
                ) : (
                  <Typography className={classes.configProperty}>
                    {formatConfigTitle(selectedProperty, selectedDataType)}
                  </Typography>
                )}
              </Box>
              <MuiBox className={classes.configTextBox}>
                {(selectedDataType === 'object' || selectedDataType === 'array' || selectedDataType === 'string') && (
                  <InputField
                    multiline
                    rows={19}
                    size={'medium'}
                    sx={{ minWidth: '400px', height: '470px', borderRadius: '25px' }}
                    value={
                      propertyValue && typeof propertyValue === 'object'
                        ? JSON.stringify(propertyValue)
                        : String(propertyValue)
                    }
                    onChange={(e) => {
                      resetError();
                      setPropertyValue(e.target.value);
                    }}
                    autoFocus
                  />
                )}
                {selectedDataType === 'number' && (
                  <InputField
                    type="text"
                    size="medium"
                    align="center"
                    fullWidth
                    autoFocus
                    value={propertyValue || propertyValue === 0 ? propertyValue.toString() : ''}
                    onChange={(e) => {
                      resetError();
                      setPropertyValue(e.target.value);
                    }}
                  />
                )}
                {selectedDataType === 'boolean' && (
                  <Switch
                    checked={propertyValue === true || propertyValue === 'true'}
                    checkedLabel="TRUE"
                    uncheckedLabel="FALSE"
                    onChange={(e) => {
                      resetError();
                      setPropertyValue(e.target.checked.toString());
                    }}
                  />
                )}
                {selectedDataType === 'integration' && (
                  <SelectDropdown
                    placeholder={`Select an Integration`}
                    items={getSortedList(integrations, 'name')}
                    selectedItem={selectedIntegration}
                    onChange={(item: Integration) => setSelectedIntegration(item)}
                    itemToString={(item: Integration) => (item ? item.name : '')}
                  />
                )}
                {selectedDataType === 'configuration' && (
                  <SelectDropdown
                    placeholder={`Select a Configuration`}
                    items={getSortedList(configurations, 'name')}
                    selectedItem={selectedConfiguration}
                    onChange={(item: Configuration) => setSelectedConfiguration(item)}
                    itemToString={(item: Configuration) => (item ? item.name : '')}
                  />
                )}
                {selectedDataType === 'clientIntegration' && (
                  <SelectDropdown
                    placeholder={`Select a Client Integration`}
                    items={getSortedList(clientIntegrations, 'name')}
                    selectedItem={selectedCI}
                    onChange={(item: ClientIntegration) => setSelectedCI(item)}
                    itemToString={(item: ClientIntegration) => (item ? item.name : '')}
                  />
                )}
                {selectedDataType === 'area' && (
                  <SelectDropdown
                    placeholder={`Select an Area`}
                    items={getSortedList(areas, 'name')}
                    selectedItem={selectedArea}
                    onChange={(item: Area) => setSelectedArea(item)}
                    itemToString={(item: Area) => (item ? item.name : '')}
                  />
                )}
              </MuiBox>
              <Box margin="1 0 0">
                <Typography color={color.RED} fontSize={14}>
                  {error}
                </Typography>
              </Box>
              {/* button */}
              <MuiBox className={classes.saveButtonBox}>
                <Button customSize="small" appearance="primary" onClick={onConfirm} className={classes.saveButton}>
                  Save
                </Button>
              </MuiBox>
            </MuiBox>
          </Card>
        </Grid>
      </Drawer>
    </>
  );
};
